/**
 * Collapsible turns (#220). By default the newest N turns of a pane are expanded (N is the person's preference,
 * default 2) and the rest collapse to one line; unsent drafts and the turn being replied to are always expanded.
 * A turn someone opened or closed by hand stays that way for the session, per person per case (sessionStorage:
 * a per-viewer convenience, never shared state). Owner: feat/newest-first (#207, #220).
 */
import { safeStorage } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import { sessionQuery } from '../../lib/api/queries'
import { notificationPrefsQuery } from '../me/queries'

export const DEFAULT_EXPANDED_TURNS = 2

export interface CollapsibleTurn {
  id: string
  /** An unsent Desk draft: always expanded. */
  draft?: unknown
  /** Automatic replies stay collapsed unless the viewer explicitly opens one. */
  autoReply?: unknown
  /** Robot mail (#727): collapsed like an automatic reply. */
  automated?: unknown
}

/** The newest `count` turns (input is chronological): open by default, collapsible by hand. */
export function defaultOpenIds(turns: readonly CollapsibleTurn[], count: number): Set<string> {
  const substantive = turns.filter((turn) => !turn.autoReply && !turn.automated)
  return new Set(substantive.slice(Math.max(0, substantive.length - Math.max(0, count))).map((t) => t.id))
}

/** Unsent drafts and the turn being replied to: always expanded, so they have no collapse control. */
export function forcedOpenIds(turns: readonly CollapsibleTurn[], replyingTo?: string | null): Set<string> {
  return new Set(turns.filter((t) => t.draft || t.id === replyingTo).map((t) => t.id))
}

function sessionStore(): Storage | null {
  try {
    return globalThis.sessionStorage ?? null
  } catch {
    return null
  }
}

function readChoices(key: string): Record<string, boolean> {
  try {
    const raw = sessionStore()?.getItem(key)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

function writeChoices(key: string, value: Record<string, boolean>): void {
  try {
    sessionStore()?.setItem(key, JSON.stringify(value))
  } catch {
    /* convenience only */
  }
}

export const EXPANDED_TURNS_KEY = 'patchbay:expanded-turns'

export interface ExpandedTurnPreference {
  count: number
  error: string | null
  retry: () => void
}

/** The person's "expanded by default" count plus a visible failure state for the server preference read. */
export function useExpandedTurnPreference(): ExpandedTurnPreference {
  const prefs = useQuery({ ...notificationPrefsQuery(), retry: false, staleTime: 5 * 60_000 })
  const server = prefs.data?.expanded_turns
  React.useEffect(() => {
    if (typeof server === 'number') safeStorage.setItem(EXPANDED_TURNS_KEY, String(server))
  }, [server])
  const raw = safeStorage.getItem(EXPANDED_TURNS_KEY)
  const local = raw === null ? Number.NaN : Number(raw)
  const count = typeof server === 'number' && server >= 0 ? server : Number.isInteger(local) && local >= 0 ? local : DEFAULT_EXPANDED_TURNS
  return {
    count,
    error: prefs.isError ? describeError(prefs.error) : null,
    retry: () => void prefs.refetch(),
  }
}

/** Count-only compatibility wrapper for portal and isolated timeline consumers. */
export function useExpandedTurnCount(): number {
  return useExpandedTurnPreference().count
}

export interface TurnCollapse {
  /** Is this turn expanded? `defaults` is the pane's default-open set (see defaultOpenIds). */
  isOpen: (id: string, defaults: Set<string>) => boolean
  toggle: (id: string, defaults: Set<string>) => void
  /** Expand or collapse every listed turn (Expand all / Collapse all). */
  setAll: (ids: readonly string[], open: boolean) => void
}

/** The session's hand-made choices for one case, for the signed-in person. */
export function useTurnCollapse(caseKey: string): TurnCollapse {
  // Read the cached session only (the shell already loaded it); never a fetch of its own.
  const session = useQuery({ ...sessionQuery(), enabled: false })
  const who = session.data?.me?.id ?? 'me'
  const key = `patchbay:turns:${who}:${caseKey}`
  const [choices, setChoices] = React.useState<Record<string, boolean>>(() => readChoices(key))
  React.useEffect(() => setChoices(readChoices(key)), [key])

  const save = React.useCallback(
    (update: (current: Record<string, boolean>) => Record<string, boolean>) =>
      setChoices((current) => {
        const next = update(current)
        writeChoices(key, next)
        return next
      }),
    [key],
  )
  const isOpen = React.useCallback((id: string, defaults: Set<string>) => choices[id] ?? defaults.has(id), [choices])
  const toggle = React.useCallback(
    (id: string, defaults: Set<string>) => save((c) => ({ ...c, [id]: !(c[id] ?? defaults.has(id)) })),
    [save],
  )
  const setAll = React.useCallback(
    (ids: readonly string[], open: boolean) =>
      save((c) => {
        const next = { ...c }
        for (const id of ids) next[id] = open
        return next
      }),
    [save],
  )
  return { isOpen, toggle, setAll }
}
